import { z } from 'zod';
import { dateOnlySchema, idSchema, patchOf } from './common';
import { genderSchema } from './members';

/** The membership a member stands on now (`membershipStandings`): active first, then frozen, suspended, pending, expired; NONE when they hold none of those. */
export const SEGMENT_MEMBERSHIP_STATES = ['ACTIVE', 'PAUSED', 'SUSPENDED', 'PENDING', 'EXPIRED', 'NONE'] as const;
export type SegmentMembershipState = (typeof SEGMENT_MEMBERSHIP_STATES)[number];

export const SEGMENT_MAX_RULES = 20;

const days = z.number().int().min(1).max(3650);
const ids = z.array(idSchema).min(1).max(50);
const optionalDate = dateOnlySchema.nullable().default(null);
const optionalAge = z.number().int().min(0).max(120).nullable().default(null);

function inOrder(low: string | number | null, high: string | number | null): boolean {
  return low === null || high === null || low <= high;
}

/**
 * Every rule a segment can hold. A member is in the segment when all of its rules hold. A rule is data only: the
 * resolver (`segmentWhere` in `@bulk/core`) turns each type into SQL, so adding a rule is a shape here and a case there.
 */
export const segmentRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hasTag'), tagIds: ids.describe('Carries at least one of these tags') }),
  z.object({ type: z.literal('lacksTag'), tagIds: ids.describe('Carries none of these tags') }),
  z.object({ type: z.literal('membershipStatus'), statuses: z.array(z.enum(SEGMENT_MEMBERSHIP_STATES)).min(1).max(SEGMENT_MEMBERSHIP_STATES.length).describe('The membership the member stands on now is in one of these states') }),
  z.object({ type: z.literal('plan'), planIds: ids.describe('The membership the member stands on now is on one of these plans') }),
  z.object({ type: z.literal('visitedBranch'), locationIds: ids, withinDays: days.nullable().default(null).describe('Only visits in this many days up to now; null for any visit') }),
  z.object({ type: z.literal('joined'), from: optionalDate, to: optionalDate }).refine((r) => r.from !== null || r.to !== null, { message: 'Give a first or a last day', path: ['from'] }).refine((r) => inOrder(r.from, r.to), { message: 'The first day is after the last', path: ['to'] }),
  z.object({ type: z.literal('lastVisit'), after: optionalDate, before: optionalDate }).refine((r) => r.after !== null || r.before !== null, { message: 'Give a day', path: ['after'] }).refine((r) => inOrder(r.after, r.before), { message: 'The first day is after the last', path: ['before'] }),
  z.object({ type: z.literal('noVisitWithin'), days }),
  z.object({ type: z.literal('membershipEndsWithin'), days }),
  z.object({ type: z.literal('membershipEndedWithin'), days }),
  z.object({ type: z.literal('birthdayThisMonth') }),
  z.object({ type: z.literal('gender'), genders: z.array(genderSchema).min(1).max(4) }),
  z.object({ type: z.literal('age'), min: optionalAge, max: optionalAge }).refine((r) => r.min !== null || r.max !== null, { message: 'Give a lowest or a highest age', path: ['min'] }).refine((r) => inOrder(r.min, r.max), { message: 'The lowest age is above the highest', path: ['max'] }),
  z.object({ type: z.literal('smsOffers'), granted: z.boolean().describe('true: agreed to offers by SMS; false: has not') }),
  z.object({ type: z.literal('balanceBelow'), amountMinor: z.number().int().min(1).max(100_000_000).describe('Prepaid balance under this, in minor units; a member without a balance has 0') }),
]);
export type SegmentRule = z.infer<typeof segmentRuleSchema>;
export type SegmentRuleInput = z.input<typeof segmentRuleSchema>;
export type SegmentRuleType = SegmentRule['type'];

export const SEGMENT_RULE_TYPES: readonly SegmentRuleType[] = segmentRuleSchema.options.map((option) => option.shape.type.value);

export const segmentRulesSchema = z.array(segmentRuleSchema).max(SEGMENT_MAX_RULES);

export const createSegmentSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullable().optional(),
  rules: segmentRulesSchema,
});
export type CreateSegmentInput = z.infer<typeof createSegmentSchema>;

export const updateSegmentSchema = patchOf(createSegmentSchema);
export type UpdateSegmentInput = z.infer<typeof updateSegmentSchema>;

export const segmentSchema = z.object({
  id: idSchema,
  name: z.string(),
  description: z.string().nullable(),
  rules: segmentRulesSchema.nullable().describe('null when the stored rules no longer validate; using the segment is then refused (422 SEGMENT_INVALID) until it is saved again'),
  memberCount: z.number().int().nullable().describe('Members in the segment now; null when its rules no longer validate'),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SegmentDto = z.infer<typeof segmentSchema>;

export const segmentCountInputSchema = z.object({ rules: segmentRulesSchema });
export type SegmentCountInput = z.infer<typeof segmentCountInputSchema>;

export const segmentCountSchema = z.object({ count: z.number().int() });
export type SegmentCountDto = z.infer<typeof segmentCountSchema>;
